import { useEffect, useMemo, type CSSProperties } from 'react'
import { useNotificationStore, type AppNotification } from '@/store/notifications'
import { cn } from '@/lib/cn'

const TIME = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})
const DAY = new Intl.DateTimeFormat(undefined, { weekday: 'long', day: 'numeric', month: 'long' })

/** Midnight-relative, so "Today" turns over at midnight rather than after 24h. */
function dayLabel(at: number): string {
  const start = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const days = Math.round((start(new Date()) - start(new Date(at))) / 86_400_000)
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  return DAY.format(at)
}

function groupByDay(items: AppNotification[]): [string, AppNotification[]][] {
  const groups: [string, AppNotification[]][] = []
  for (const item of items) {
    const label = dayLabel(item.at)
    const last = groups[groups.length - 1]
    if (last && last[0] === label) last[1].push(item)
    else groups.push([label, [item]])
  }
  return groups
}

// Plain information carries no label: "Note" over every entry said nothing.
const LEVEL_LABEL: Record<AppNotification['level'], string | null> = {
  positive: 'Done',
  negative: 'Failed',
  warning: 'Needs you',
  info: null,
}

/**
 * `/notifications` — everything the app has told you, kept.
 *
 * Laid out as a ledger rather than a stack of cards: a monospace time gutter
 * down the left, each entry hanging off it under the day it happened. The
 * monospace is the same face the revealed keys use, which is the one place in
 * this product where an exact string matters — and it is what these are for
 * too, now that they can be read at leisure and copied.
 *
 * Not behind AccountPage's auth guard on purpose. "Enter your username" is
 * raised while signed out, and a page that redirects home would be the one
 * place those messages could never be read.
 */
export function NotificationsPage() {
  const items = useNotificationStore((state) => state.items)
  const unread = useNotificationStore((state) => state.unread)
  const markAllRead = useNotificationStore((state) => state.markAllRead)
  const remove = useNotificationStore((state) => state.remove)
  const clear = useNotificationStore((state) => state.clear)

  // Reading the page is what makes them read. Deferred so the unread styling
  // survives first paint — otherwise arriving from the bell shows nothing new.
  useEffect(() => {
    if (unread === 0) return
    const timer = window.setTimeout(markAllRead, 1200)
    return () => window.clearTimeout(timer)
  }, [unread, markAllRead])

  const groups = useMemo(() => groupByDay(items), [items])

  return (
    <div className="account-page">
      <header className="account-header rise">
        <h1 className="account-title">Notifications</h1>
        {items.length > 0 ? (
          <div className="account-action">
            <button type="button" className="notification-clear" onClick={clear}>
              Clear all
            </button>
          </div>
        ) : null}
      </header>

      <div className="account-body">
        {items.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">Nothing yet</div>
            <div className="empty-state-body">
              Entries, keys you win and anything that needs your attention are recorded here.
            </div>
          </div>
        ) : (
          <div className="notification-log rise">
            {groups.map(([label, group]) => (
              <section className="notification-day" key={label}>
                <h2 className="notification-day-label">{label}</h2>

                {group.map((item, index) => (
                  <article
                    key={item.id}
                    className={cn('notification-row', 'rise', item.level, !item.read && 'unread')}
                    // Capped: a staged run reads as arrival over the first few
                    // rows and as a slow page everywhere after that.
                    style={{ '--d': `${Math.min(index, 6) * 45}ms` } as CSSProperties}
                  >
                    <time className="notification-time" dateTime={new Date(item.at).toISOString()}>
                      {TIME.format(item.at)}
                    </time>

                    <div className="notification-content">
                      {LEVEL_LABEL[item.level] ? (
                        <span className="notification-level">{LEVEL_LABEL[item.level]}</span>
                      ) : null}
                      <p className="notification-message">{item.message}</p>
                      {item.action ? (
                        <button
                          type="button"
                          className="notification-action"
                          onClick={item.action.onClick}
                        >
                          {item.action.label}
                        </button>
                      ) : null}
                    </div>

                    <button
                      type="button"
                      className="notification-dismiss"
                      aria-label="Remove notification"
                      onClick={() => remove(item.id)}
                    >
                      <svg viewBox="0 0 16 16" aria-hidden="true">
                        <path d="M4.6 4.6 11.4 11.4M11.4 4.6 4.6 11.4" />
                      </svg>
                    </button>
                  </article>
                ))}
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
